import config from 'config';
import Polly from 'aws-sdk/clients/polly';
import { DOMParser, XMLSerializer } from 'xmldom';

import storeManager from '../store/StoreManager';
import apiService from './api/ApiService';
import rangyService from './RangyService';
import textToSpeechActions from '../actions/TextToSpeechActions';
import environmentNames from '../qti/environmentNames';
import utilService from './UtilService';

let spokenSound;

/**
 * Neural TTS is used for more natural voices. Some languages, like Finnish, only have Neural voices.
 * Big benefit of Neural is quality, drawback is cost.
 * https://docs.aws.amazon.com/polly/latest/dg/NTTS-main.html
  */
const pollyEngine = {
    STANDARD: 'standard', // the old Polly engine
    NEURAL: 'neural', // newer Polly engine with more natural sounding voice.
};

export const pollyVoiceLanguageMap = {
    'da-DK': {
        voiceId: 'Naja',
        engine: pollyEngine.STANDARD,
    },
    'en-US': {
        voiceId: 'Amy',
        engine: pollyEngine.STANDARD,
    },
    'fr-FR': {
        voiceId: 'Celine',
        engine: pollyEngine.STANDARD,
    },
    'nb-NO': {
        voiceId: 'Liv',
        engine: pollyEngine.STANDARD,
    },
    'nl-NL': {
        voiceId: 'Lotte',
        engine: pollyEngine.STANDARD,
    },
    'nn-NO': {
        voiceId: 'Liv',
        engine: pollyEngine.STANDARD,
    },
    'ro-RO': {
        voiceId: 'Carmen',
        engine: pollyEngine.STANDARD,
    },
    'sv-SE': {
        voiceId: 'Astrid',
        engine: pollyEngine.STANDARD,
    },
    'pl-PL': {
        voiceId: 'Jacek',
        engine: pollyEngine.STANDARD,
    },
    'es-CO': {
        voiceId: 'Miguel',
        engine: pollyEngine.STANDARD,
    },
    'de-DE': {
        voiceId: 'Marlene',
        engine: pollyEngine.STANDARD,
    },
    'is-IS': {
        voiceId: 'Dora',
        engine: pollyEngine.STANDARD,
    },
    'fi-FI': {
        voiceId: 'Suvi',
        engine: pollyEngine.NEURAL,
    },
};

const TextToSpeechService = {
    /**
     * A method that creates a simpler SSML document based on the html of the selection.
     * @param parentElement
     * @param ssmlDoc
     */
    parseHTMLNodes(parentElement, ssmlDoc) {
        Array.from(parentElement.childNodes).forEach((node) => {
            if (node.nodeType === 1) {
                const nodeName = node.nodeName.toLowerCase();

                if (['p', 'li'].indexOf(nodeName) !== -1) {
                    // p and li elements should be represented as p elements in SSML for correct pause.
                    const paragraph = ssmlDoc.createElement('p');
                    const paragraphText = ssmlDoc.createTextNode(node.textContent);
                    paragraph.appendChild(paragraphText);
                    ssmlDoc.firstChild.appendChild(paragraph);
                } else {
                    this.parseHTMLNodes(node, ssmlDoc);
                }
            } else if (node.nodeType === 3 && node.textContent.trim() !== '') {
                // All text nodes should be added as text nodes to the SSML document
                const newTextNode = ssmlDoc.createTextNode(node.textContent);
                ssmlDoc.firstChild.appendChild(newTextNode);
            }
        });
    },
    convertToSSML(htmlString) {
        const parser = new DOMParser();
        const serializer = new XMLSerializer();
        const htmlStringWithRoot = `<body>${htmlString}</body>`;
        const ssmlDoc = parser.parseFromString('<speak></speak>', 'text/html');
        const htmlDoc = parser.parseFromString(htmlStringWithRoot, 'text/html');
        this.parseHTMLNodes(htmlDoc.firstChild, ssmlDoc);
        return serializer.serializeToString(ssmlDoc);
    },
    verifySelection() {
        const rangy = rangyService.getRangy();
        const state = storeManager.getState();
        if (!state.textToSpeech.isEnabled) {
            return;
        }
        // TODO check for events in places where sound shouldn't be played, like CKEditor
        const selection = rangy.getSelection();
        const hasValidSelection = !!selection.toString();
        storeManager.dispatch(textToSpeechActions.setHasValidSelection(hasValidSelection));
        if (hasValidSelection && !state.textToSpeech.isPlaying) {
            this.removeSound();
            this.playSelection();
        }
    },
    playSelection() {
        const rangy = rangyService.getRangy();
        const state = storeManager.getState();
        if (!state.textToSpeech.hasValidSelection) {
            return;
        }
        if (spokenSound) {
            // Resume the selected sound if still active
            spokenSound.play();
            return;
        }
        const ttsVoice = pollyVoiceLanguageMap[state.session.language];
        const selection = rangy.getSelection();
        const html = selection.toHtml();
        this.removeSound();
        if (html !== '') {
            const ssml = this.convertToSSML(html);
            console.log(ssml);
            this.preparePollyAudio(ssml, ttsVoice);
        }
    },
    playSound() {
        spokenSound.play();
    },
    pauseSound() {
        spokenSound.pause();
    },
    removeSound() {
        if (!spokenSound) {
            return;
        }
        try {
            spokenSound.pause();
            spokenSound.removeEventListener('playing', this.onSoundIsPlaying);
            spokenSound.removeEventListener('pause', this.onSoundIsPaused);
            spokenSound = undefined;
            storeManager.dispatch(textToSpeechActions.setTextToSpeechIsPlaying(false));
            storeManager.dispatch(textToSpeechActions.setIsAudioLoaded(false));
        } catch (error) {
            console.log('Unable to stop sound');
        }
    },
    removeSoundAndClearSelection() {
        if (process.env.NODE_ENV === environmentNames.UNIT_TESTING) {
            return;
        }
        const rangy = rangyService.getRangy();
        const state = storeManager.getState();
        this.removeSound();

        if (state.textToSpeech.hasValidSelection) {
            const selection = rangy.getSelection();
            selection.removeAllRanges();
            storeManager.dispatch(textToSpeechActions.setHasValidSelection(false));
        }
    },
    preparePollyAudio(ssml, voice) {
        const needsFileGenerated = utilService.isSebForMac() || utilService.isSafari();

        if (needsFileGenerated) {
            // Safari and SEB on mac needs a s3 url generated
            this.preparePollyAudioFromUrl(ssml, voice);
        } else {
            // Generate url from the stream
            this.preparePollyAudioFromStream(ssml, voice);
        }
    },
    preparePollyAudioFromUrl(ssml, voice) {
        apiService
            .getTTSAudioUrl(ssml, voice)
            .then((response) => {
                this.initPollyAudio(response.s3URL);
            })
            .catch(() => {
                this.removeSound();
                storeManager.dispatch(textToSpeechActions.setHasValidSelection(false));
            });
    },
    preparePollyAudioFromStream(ssml, voice) {
        const params = {
            Text: ssml,
            TextType: 'ssml',
            OutputFormat: 'mp3',
            VoiceId: voice.voiceId,
            Engine: voice.engine,
        };

        if (config.pollyTestAccessKey) {
            // For using dev server in local development
            const pollyClient = new Polly({
                region: 'eu-west-1',
                accessKeyId: config.pollyTestAccessKey,
                secretAccessKey: config.pollyTestSecretKey,
            });

            this.loadPollyAudioStream(pollyClient, params);
            return;
        }

        apiService
            .getTTSAccessInfo()
            .then((response) => {
                const pollyClient = new Polly({
                    region: 'eu-west-1',
                    accessKeyId: response.tempAccessKeyId,
                    secretAccessKey: response.tempSecretAccessKey,
                    sessionToken: response.tempSessionToken,
                });

                this.loadPollyAudioStream(pollyClient, params);
            });
    },
    loadPollyAudioStream(pollyClient, params) {
        pollyClient.synthesizeSpeech(params, (err, data) => {
            if (err) {
                console.log(err.code);
                this.removeSound();
                storeManager.dispatch(textToSpeechActions.setHasValidSelection(false));
            } else if (data) {
                if (data.AudioStream instanceof Buffer) {
                    const uInt8Array = new Uint8Array(data.AudioStream);
                    const arrayBuffer = uInt8Array.buffer;
                    const blob = new Blob([arrayBuffer]);
                    const url = URL.createObjectURL(blob);
                    this.initPollyAudio(url);
                }
            }
        });
    },
    initPollyAudio(url) {
        spokenSound = new Audio(url);
        spokenSound.addEventListener('playing', this.onSoundIsPlaying);
        spokenSound.addEventListener('pause', this.onSoundIsPaused);
        storeManager.dispatch(textToSpeechActions.setIsAudioLoaded(true));
    },
    onSoundIsPlaying() {
        storeManager.dispatch(textToSpeechActions.setTextToSpeechIsPlaying(true));
    },
    onSoundIsPaused() {
        storeManager.dispatch(textToSpeechActions.setTextToSpeechIsPlaying(false));
    },
    disableTTSIfUnavailable(language) {
        if (!pollyVoiceLanguageMap[language]) {
            storeManager.dispatch(textToSpeechActions.setTextToSpeechIsEnabled(false));
        }
    },
};

export default TextToSpeechService;
